/* eslint-disable react/prop-types */
import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

// Ajuste o caminho/nome conforme onde você salvou o arquivo de traduções.
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { useSpring } from 'react-spring';
import {
  MdClose,
  MdShoppingCart,
  MdLens,
  MdLabelOutline,
  MdSearch,
} from 'react-icons/md';

import { IoGitPullRequestSharp } from 'react-icons/io5';
import {
  opIdiomaCatalogo,
  HEADER_TABLE_ITENS_IT,
  HEADER_TABLE_ITENS_PN,
  HEADER_TABLE_ITENS_CERP,
  HEADER_TABLE_ITENS_DESC,
  HEADER_TABLE_ITENS_QTD,
  HEADER_TABLE_ITENS_NT,
  HEADER_TABLE_ITENS_ADD,
} from '../../../../lib/idiomaAppOptions';

// import { toast } from 'react-toastify';
import { PDF, opPdfExport } from '../../../../lib/pdf-core';

import { initialParams } from '../../../../lib/reactRouter';
import { useScrollLock } from '../../../../lib/pageUtils';
import {
  redirectToCart,
  reduzTabelaItensHome,
  removeERPHome,
  utilizaVisualizaoFacesSistema,
} from '../../../../lib/paramUtils';
import {
  // adicionaCarrinhoArrayRequest,
  adicionaCarrinhoRequest,
  // sharedManagementSet,
} from '../../../../store/modules/cart/actions';

import history from '../../../../services/history';
import api from '../../../../services/api';

import ItemCart from '../../../../components/ItemCart';

import { emailTipo } from '../../../Email';

import {
  Container,
  Wrapper,
  ZoomArea,
  ItemArea,
  Table,
  Info,
  SVG,
  MagnifierLens,
  LupaToggle,
  NoteContainer,
  NoteContent,
  Notes,
  NotesModal,
  NoteHeader,
} from './styles';

/**
 * =====================================================================
 * DIMENSÕES DA LUPA
 * ---------------------------------------------------------------------
 * Fonte única: styles.js não repete mais nenhum destes valores, todos
 * são aplicados inline em tempo de execução.
 *
 * A lupa deixou de ser um círculo e passou a ser um cartão: em cima a
 * área ampliada (quadrada, com cantos arredondados) e embaixo a faixa
 * com a informação do item sob o cursor.
 *
 * O motivo da mudança: a caixa de informação do hover (Info) e a lupa
 * são irmãs dentro do ZoomArea, e a lupa tem z-index 30 contra o
 * z-index automático do Info — com a lupa ligada, a informação do item
 * ficava sempre escondida atrás dela. Levar a informação para dentro do
 * cartão resolve sem cobrir a imagem ampliada.
 *
 * O quadrado também aproveita melhor a área: num círculo de 280px os
 * cantos descartam cerca de 21% do que caberia, justamente onde ficam
 * as linhas e os textos horizontais dos esquemas elétricos.
 * =====================================================================
 */
const LENS_SIZE = 320;
const LENS_ZOOM = 2.8;

/** altura da faixa de informação, SEMPRE reservada (ver LENS_TOTAL_H) */
const LENS_INFO_H = 58;

/** espessura da moldura branca do cartão (border em styles.js) */
const LENS_BORDER = 3;

/**
 * Medidas EXTERNAS do cartão, as que valem para a contenção.
 *
 * A moldura entra na conta: sem ela a lupa passava da borda inferior do
 * container por 2 x 3px. E a faixa entra mesmo quando está invisível —
 * se a altura variasse conforme existe ou não item sob o cursor, o
 * cartão saltaria ao entrar e sair de cada link perto da borda de baixo.
 */
const LENS_TOTAL_W = LENS_SIZE + LENS_BORDER * 2;
const LENS_TOTAL_H = LENS_SIZE + LENS_INFO_H + LENS_BORDER * 2;

/**
 * Chave única de um item da página (o mesmo IDITEM pode aparecer mais de uma
 * vez na listagem, um registro por material).
 */
const chaveItemPagina = (item) =>
  item ? `${item.idItem}-${item.idMaterial}` : null;

const Pagina = forwardRef(
  ({ id, idPagina, zoomInRef, zoomOutRef, zoomResetRef }, ref) => {
    const carrinho = useSelector(
      (state) =>
        state.cart.items.map((item) => ({
          ...item,
          ...item.materialFabricante,
        })) || {}
    );

    /** idioma do catálogo escolhido pelo usuário (null = padrão) */
    const idiomaCatalogo = useSelector(
      (state) => state.param.idiomaCatalogo || null
    );
    const idiomaParams =
      idiomaCatalogo !== null ? { idioma: idiomaCatalogo } : {};

    /** idioma efetivo para os textos da tela (fallback para PT) */
    const idioma = idiomaCatalogo || opIdiomaCatalogo.BR;

    /**
     * Empresas que utilizam "faces do sistema" apresentam o mesmo item
     * (mesma tabela/ITEMPAGINA) em um conjunto de páginas diferentes
     * (uma por face). Nesses casos o item precisa ser reconhecido como
     * "no carrinho" mesmo quando a página atual não é exatamente a mesma
     * em que ele foi adicionado.
     */
    const usaFacesSistema = utilizaVisualizaoFacesSistema();

    /**
     * Parâmetro global 35 (REMOVE_ERP_HOME): oculta o Código ERP do material
     * na Home — aqui, a coluna "Cód. ERP" da listagem de itens da página.
     */
    const ocultaERPHome = removeERPHome();

    /**
     * Parâmetro global 34 (REDUZ_TABELA_ITENS_HOME): a listagem de itens passa
     * a exibir apenas um item por vez (o item ativo), liberando espaço em tela
     * para o desenho. Por padrão exibe o primeiro item da listagem e troca
     * conforme o usuário passa o mouse sobre os itens do desenho, mantendo o
     * último item destacado ao sair do hover.
     */
    const tabelaReduzida = reduzTabelaItensHome();

    /**
     * Verifica se dois itens (um do carrinho e um da página atual)
     * representam a mesma página, considerando o agrupamento de faces do
     * sistema: quando ativo, itens com a mesma página de origem
     * (IDPAGINA_ORIGEM) são tratados como a mesma página, mesmo estando
     * fisicamente em páginas (faces) diferentes.
     */
    const mesmaPaginaOuFace = useCallback(
      (itemA, itemB) => {
        if (Number(itemA?.idPagina) === Number(itemB?.idPagina)) return true;

        if (!usaFacesSistema) return false;

        const origemA =
          Number(itemA?.idPaginaOrigem) || Number(itemA?.idPagina);
        const origemB =
          Number(itemB?.idPaginaOrigem) || Number(itemB?.idPagina);

        return !!origemA && origemA === origemB;
      },
      [usaFacesSistema]
    );

    // const { token } = useSelector((state) => state.auth || {});
    // const { sharedManagement } = useSelector((state) => state.cart || {});

    const containerRef = useRef();
    const mappingRef = useRef([]);
    const svgRef = useRef();

    /** refs/estado da lupa de zoom sobre a imagem */
    const lensRef = useRef();
    const lensViewRef = useRef();
    const lensImgRef = useRef();
    const [magnifierSrc, setMagnifierSrc] = useState(null);

    /** liga/desliga da lupa controlado pelo usuário (botão sobre a imagem) */
    const [lupaAtiva, setLupaAtiva] = useState(false);

    const tableRef = useRef();

    const observerTarget = useRef(null);
    const lastItemSearchHandleRef = useRef(null);

    const location = useLocation();

    const [pagina, setPagina] = useState(null);
    const [itensPagina, setItensPagina] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPage, setTotalPage] = useState();

    const [itemNote, setItemNote] = useState([]);
    const [lengthNote, setLengthNote] = useState(0);

    const [showNote, setShowNote] = useState(false);

    const [itemInfo, setItemInfo] = useState(null);
    const [itemCart, setItemCart] = useState(null);

    const [itensConjuntoSelecionados, setItensConjuntoSelecionados] = useState(
      []
    );

    const [paginaMaximizada, setPaginaMaximizada] = useState(false);

    /**
     * Item exibido na listagem quando a tabela está reduzida (parâmetro 34).
     * `null` = usa o primeiro item da listagem.
     */
    const [itemAtivoChave, setItemAtivoChave] = useState(null);

    // const [zoomStep, setZoomStep] = useState(5);

    const [elementToZoom, setElementToZoom] = useState();

    const dispatch = useDispatch();

    const { lockScroll, unlockScroll } = useScrollLock();

    useEffect(() => {
      /** pega parâmetros da rota */
      const paramQuery = new URLSearchParams(location.search);

      const paramRoute = {
        ...initialParams,
        ...Object.fromEntries(paramQuery),
      };

      api
        .get(`catalogo-publico/${id}/pagina/${idPagina}`, {
          params: { ...idiomaParams },
        })
        .then((response) => {
          const { nota } = response.data;

          setPagina(response.data);
          setItemNote(() => (nota ? nota.split(';').filter(Boolean) : []));
          setLengthNote(nota ? nota.length : 0);
        });

      api
        .get(`/catalogo-publico/${id}/pagina/${idPagina}/item`, {
          params: {
            page: 1,
            ...paramRoute,
            ...idiomaParams,
          },
        })
        .then((response) => {
          setItensPagina(
            response.data.responseItemPagina.map((item) => {
              const { idItem, materialEmpresa, materialFabricante } = item;

              /** formata IDITEM */
              const fmtItem =
                String(idItem).startsWith('@') || String(idItem) === '0'
                  ? ''
                  : idItem;

              /** formata PARTNUMBER */
              const fmtPartnumber = String(
                materialFabricante.partnumber
              ).startsWith('@')
                ? ''
                : materialFabricante.partnumber;

              return {
                ...item,
                materialEmpresa,
                ...materialFabricante,
                partnumber: fmtPartnumber,
                itemResum: fmtItem,

                // Concatena os materiais da empresa
                codimateResum:
                  materialEmpresa &&
                  materialEmpresa
                    .map((material) => material.codimate)
                    .join(', '),
                selecionado: false,
                exibido: false,
              };
            })
          );

          setTotalPage(response.data.total);
          setCurrentPage(response.data.pagAtual);

          if (response.data.total === 0) {
            setTotalPage(0);
            setCurrentPage(0);
          }
        });

      window.addEventListener('resize', () => {
        /** reposiciona o desenho conforme a página é redimensionada */
        if (zoomResetRef.current && zoomResetRef.current.onclick) {
          zoomResetRef.current.onclick();
        }
      });

      // return window.removeEventListener('resize', handleSVGResize);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location, id, idPagina, zoomResetRef, idiomaCatalogo]);

    const handleImprimirPagina = useCallback(
      async (isSendMail = false, setLoading) => {
        /** cria PDF com a página do catálogo */
        PDF.criarExportacao(
          opPdfExport.CATALOGO_PAGINA,
          {
            idCatalogo: id,
            idPagina,
          },
          (filename) => {
            if (isSendMail) {
              /** carrega foto para anexar no formulário de envio de e-mail */
              // api.get(filename).then((response) => {
              //   const blobFile = new Blob([response.data], {
              //     type: 'application/pdf',
              //   });

              //   history.push('/enviar-email', {
              //     background: location,
              //     tipo: emailTipo.EMAIL_PAGINA,
              //     data: { attachment: blobFile },
              //     filename,
              //   });
              // });

              history.push('/enviar-email', {
                background: location,
                tipo: emailTipo.EMAIL_PAGINA,
                data: { filename },
              });
            } else {
              const link = document.createElement('a');
              link.setAttribute('href', filename);
              link.setAttribute('target', '_blank');
              link.setAttribute('rel', 'noopener noreferrer');

              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }
          },
          // cbBefore
          () => setLoading(true),
          // cbAfter
          () => setLoading(false)
        );
      },
      [location, id, idPagina]
    );

    const handleRedimensionarPagina = useCallback(
      (maximizado) => {
        setTimeout(() => {
          // if (maximizado) {
          //   const { width } = containerRef.current.getBoundingClientRect();

          //   setZoomStep(Math.trunc(width * 0.02));
          // } else {
          //   setZoomStep(5);
          // }

          if (zoomResetRef.current) {
            zoomResetRef.current.onclick();
          }
        }, 100);

        setPaginaMaximizada(maximizado);
      },
      [zoomResetRef]
    );

    useImperativeHandle(ref, () => ({
      handleImprimirPagina,
      handleRedimensionarPagina,
    }));

    /**
     * Página de origem (IDPAGINA_ORIGEM) representativa dos itens desta
     * página. Como, em um face do sistema, todos os itens de uma mesma
     * página compartilham a mesma origem, basta qualquer item já
     * carregado para identificá-la — evitando depender de um IDITEM
     * específico já ter sido carregado pela paginação da lista de itens
     * (que carrega 10 por vez), o que fazia a pintura falhar para itens
     * ainda não paginados.
     */
    /** volta para o primeiro item ao trocar de página/catálogo */
    useEffect(() => {
      setItemAtivoChave(null);
    }, [id, idPagina]);

    /**
     * Itens renderizados na listagem. Com o parâmetro 34 ativo exibe somente o
     * item ativo (primeiro da listagem enquanto nenhum item do desenho foi
     * percorrido pelo mouse).
     */
    const itensExibidos = useMemo(() => {
      if (!tabelaReduzida) return itensPagina;

      if (!itensPagina.length) return [];

      const itemAtivo = itemAtivoChave
        ? itensPagina.find((item) => chaveItemPagina(item) === itemAtivoChave)
        : null;

      return [itemAtivo || itensPagina[0]];
    }, [tabelaReduzida, itensPagina, itemAtivoChave]);

    const paginaOrigemAtual = useMemo(() => {
      const encontrado = itensPagina.find((item) => item?.idPaginaOrigem);
      return encontrado ? encontrado.idPaginaOrigem : undefined;
    }, [itensPagina]);

    const handleItemMouseEnter = useCallback((idItem) => {
      mappingRef.current.map((relacionamento) => {
        if (relacionamento.idItem === idItem) {
          relacionamento.node.classList.add('selecting');
        }
        return relacionamento;
      });
    }, []);

    const handleItemMouseLeave = useCallback((idItem) => {
      mappingRef.current.map((relacionamento) => {
        if (relacionamento.idItem === idItem) {
          // if (!relacionamento.selected && !relacionamento.inCart) {
          //   relacionamento.node.classList.remove('selecting');
          // }
          if (
            !relacionamento.selected &&
            !relacionamento.inCart &&
            !relacionamento.node.classList.contains('selecting-conjunto')
          ) {
            relacionamento.node.classList.remove('selecting');
          }
        }
        return relacionamento;
      });
    }, []);

    const handleRelacionar = useCallback(() => {
      /** pega parâmetros da rota */
      const paramQuery = new URLSearchParams(location.search);

      const paramRoute = {
        ...Object.fromEntries(paramQuery),
      };

      const { IDI: itemSearch } = paramRoute || {};

      /** SELECTING: ITEM QUE ESTAO NO CARRINHO */
      mappingRef.current.forEach((mapeamento) => {
        const noCarrinho = !!carrinho.find(
          (itemCarrinho) =>
            Number(itemCarrinho.idCatalogo) === id &&
            mesmaPaginaOuFace(itemCarrinho, {
              idPagina,
              idPaginaOrigem: paginaOrigemAtual,
            }) &&
            itemCarrinho.idItem === mapeamento.idItem
        );

        mapeamento.inCart = noCarrinho;

        if (noCarrinho) {
          mapeamento.node.classList.add('selecting');
        } else if (
          !mapeamento.selected &&
          !mapeamento.node.classList.contains('selecting-conjunto')
        ) {
          mapeamento.node.classList.remove('selecting');
        }
      });

      /** SELECTING: ITEM PESQUISADO */
      // let itemPaginaExt = null;
      // if (itemSearch) {
      if (itemSearch && lastItemSearchHandleRef.current !== itemSearch) {
        const [
          ,
          idPaginaSearch,
          idItemSearch,
          idMaterialSearch,
        ] = itemSearch.split('.');

        if (idItemSearch && idMaterialSearch) {
          // itemPaginaExt = itensPagina.find(
          const itemPaginaExt = itensPagina.find(
            (i) =>
              String(idItemSearch) === String(i.idItem) &&
              Number(idPaginaSearch) === Number(i.idPagina) &&
              (String(idMaterialSearch) === String(i.idMaterial) ||
                (i.materialEmpresa.length > 0 &&
                  i.materialEmpresa.find(
                    (f) => String(idMaterialSearch) === String(f.idMaterialERP)
                  )))
          );

          if (itemPaginaExt) {
            const mapping = mappingRef.current.find(
              (item) => String(item.idItem) === String(itemPaginaExt.idItem)
            );

            // if (mapping) mapping.node.onclick();
            if (mapping) {
              lastItemSearchHandleRef.current = itemSearch;
              mapping.node.onclick();
            }
          }
        }
      }
      // setItemCart(itemPaginaExt);
    }, [
      location,
      id,
      idPagina,
      carrinho,
      itensPagina,
      mesmaPaginaOuFace,
      paginaOrigemAtual,
    ]);

    /**
     * Repinta os itens no desenho sempre que o carrinho muda (adição de item
     * individual ou de conjunto/kit), sem precisar sair e voltar. Antes o
     * handleRelacionar só era chamado no afterInjection do SVG. O guard evita
     * rodar antes do SVG estar injetado (nesse caso o afterInjection pinta).
     */
    useEffect(() => {
      if (mappingRef.current && mappingRef.current.length) {
        handleRelacionar();
      }
    }, [handleRelacionar]);

    const limparClassesSelecaoNode = useCallback((node) => {
      if (!node) return;

      node.classList.remove('selecting');
      node.classList.remove('selecting-conjunto');
    }, []);

    const limparSelecoesTemporarias = useCallback(() => {
      mappingRef.current.forEach((relacionamento) => {
        if (!relacionamento.inCart) {
          relacionamento.selected = false;
          limparClassesSelecaoNode(relacionamento.node);
        } else {
          relacionamento.node.classList.remove('selecting-conjunto');
        }
      });
    }, [limparClassesSelecaoNode]);

    // const handleItemFechar = useCallback(
    //   (data) => {
    //     const { idItem } = itemCart;

    //     const itemIndex = itensPagina.findIndex(
    //       (item) => item.idItem === idItem
    //     );

    //     if (itemIndex >= 0) {
    //       itensPagina[itemIndex].exibido = false;
    //       itensPagina[itemIndex].selecionado = false;

    //       const relacionamentos = mappingRef.current.filter(
    //         (item) => item.idItem === idItem
    //       );
    //       relacionamentos.forEach((relacionamento) => {
    //         relacionamento.selected = false;
    //         if (data) {
    //           relacionamento.inCart = true;
    //         }

    //         // if (!relacionamento.inCart) {
    //         //   relacionamento.node.classList.remove('selecting');
    //         // }

    //         if (!relacionamento.inCart) {
    //           limparClassesSelecaoNode(relacionamento.node);
    //           relacionamento.node.classList.remove('selecting');
    //           relacionamento.node.classList.remove('selecting-conjunto');
    //         }
    //       });
    //     }

    //     setItensConjuntoSelecionados([]);

    //     setItemCart(null);
    //   },
    //   [itemCart, itensPagina]
    // );

    const limparParametrosItemBusca = useCallback(() => {
      const params = new URLSearchParams(location.search);
      let hasChanged = false;

      ['IDI', 'ERP'].forEach((key) => {
        if (params.has(key)) {
          params.delete(key);
          hasChanged = true;
        }
      });

      if (hasChanged) {
        const search = params.toString();
        history.replace({
          pathname: location.pathname,
          search: search ? `?${search}` : '',
        });
      }

      lastItemSearchHandleRef.current = null;
    }, [location.pathname, location.search]);

    const handleItemFechar = useCallback(
      (data) => {
        if (!itemCart) return;

        const { idItem } = itemCart;

        setItensPagina((prevItens) =>
          prevItens.map((item) =>
            item.idItem === idItem
              ? { ...item, exibido: false, selecionado: false }
              : item
          )
        );

        const relacionamentos = mappingRef.current.filter(
          (item) => item.idItem === idItem
        );

        relacionamentos.forEach((relacionamento) => {
          relacionamento.selected = false;

          if (data) {
            relacionamento.inCart = true;
          }

          relacionamento.node.classList.remove('selecting-conjunto');

          if (!relacionamento.inCart) {
            relacionamento.node.classList.remove('selecting');
          }
        });

        mappingRef.current.forEach((relacionamento) => {
          relacionamento.node.classList.remove('selecting-conjunto');
        });

        setItensConjuntoSelecionados([]);
        setItemCart(null);
        limparParametrosItemBusca();
      },
      // [itemCart, setItensPagina]
      [itemCart, setItensPagina, limparParametrosItemBusca]
    );

    const encontrarItemNaPagina = useCallback(
      ({ idItem, idMaterial }) =>
        itensPagina.find(
          (item) =>
            String(item.idItem) === String(idItem) &&
            Number(item.idMaterial) === Number(idMaterial)
        ),
      [itensPagina]
    );

    // const montarItensConjunto = useCallback(
    //   (itemSelecionado) => {
    //     if (!itemSelecionado) {
    //       return {
    //         itemPrincipal: null,
    //         itensConjunto: [],
    //       };
    //     }

    //     const conjuntoReferencia =
    //       itemSelecionado.conjuntoReferencia ||
    //       itensPagina.find(
    //         (itemPagina) =>
    //           itemPagina.isConjunto &&
    //           itemPagina.conjuntoItens?.some(
    //             (filho) =>
    //               String(filho.idItem) === String(itemSelecionado.idItem) &&
    //               Number(filho.idMaterial) ===
    //                 Number(itemSelecionado.idMaterial)
    //           )
    //       )?.conjuntoReferencia;

    //     const itemOrigemConjunto = conjuntoReferencia
    //       ? encontrarItemNaPagina({
    //           idItem: conjuntoReferencia.idItemOri,
    //           idMaterial: conjuntoReferencia.idMaterialOri,
    //         })
    //       : null;

    //     const listaBaseConjunto =
    //       itemOrigemConjunto?.conjuntoItens ||
    //       itemSelecionado?.conjuntoItens ||
    //       [];

    //     const itensResolvidos = listaBaseConjunto
    //       .map((conjuntoItem) => encontrarItemNaPagina(conjuntoItem))
    //       .filter(Boolean);

    //     const itemPrincipal = itemOrigemConjunto || itemSelecionado;

    //     const itemSelecionadoEhOrigem =
    //       String(itemPrincipal?.idItem) === String(itemSelecionado.idItem) &&
    //       Number(itemPrincipal?.idMaterial) ===
    //         Number(itemSelecionado.idMaterial);

    //     let itensConjunto = itensResolvidos.filter(
    //       (item) =>
    //         !(
    //           String(item.idItem) === String(itemPrincipal?.idItem) &&
    //           Number(item.idMaterial) === Number(itemPrincipal?.idMaterial)
    //         )
    //     );

    //     if (!itemSelecionadoEhOrigem) {
    //       const itemSelecionadoJaExisteNaLista = itensConjunto.some(
    //         (item) =>
    //           String(item.idItem) === String(itemSelecionado.idItem) &&
    //           Number(item.idMaterial) === Number(itemSelecionado.idMaterial)
    //       );

    //       if (!itemSelecionadoJaExisteNaLista) {
    //         itensConjunto = [itemSelecionado, ...itensConjunto];
    //       }
    //     } else {
    //       itensConjunto = itensConjunto.filter(
    //         (item) =>
    //           !(
    //             String(item.idItem) === String(itemSelecionado.idItem) &&
    //             Number(item.idMaterial) === Number(itemSelecionado.idMaterial)
    //           )
    //       );
    //     }

    //     return {
    //       itemPrincipal: itemPrincipal
    //         ? {
    //             ...itemPrincipal,
    //             imagemCatalogo: pagina?.url || itemPrincipal.imagemCatalogo,
    //           }
    //         : null,
    //       itensConjunto: itensConjunto.map((item) => ({
    //         ...item,
    //         imagemCatalogo: pagina?.url || item.imagemCatalogo,
    //       })),
    //     };
    //   },
    //   [encontrarItemNaPagina, itensPagina, pagina]
    // );

    // const montarItensConjunto = useCallback(
    //   (itemSelecionado) => {
    //     if (!itemSelecionado) {
    //       return {
    //         itemPrincipal: null,
    //         itensConjunto: [],
    //       };
    //     }

    //     const conjuntoReferencia =
    //       itemSelecionado.conjuntoReferencia ||
    //       itensPagina.find(
    //         (itemPagina) =>
    //           itemPagina.isConjunto &&
    //           itemPagina.conjuntoItens?.some(
    //             (filho) =>
    //               String(filho.idItem) === String(itemSelecionado.idItem) &&
    //               Number(filho.idMaterial) ===
    //                 Number(itemSelecionado.idMaterial)
    //           )
    //       )?.conjuntoReferencia;

    //     const itemOrigemConjunto = conjuntoReferencia
    //       ? encontrarItemNaPagina({
    //           idItem: conjuntoReferencia.idItemOri,
    //           idMaterial: conjuntoReferencia.idMaterialOri,
    //         })
    //       : null;

    //     const listaBaseConjunto =
    //       itemOrigemConjunto?.conjuntoItens ||
    //       itemSelecionado?.conjuntoItens ||
    //       [];

    //     const itensResolvidos = listaBaseConjunto
    //       .map((conjuntoItem) => encontrarItemNaPagina(conjuntoItem))
    //       .filter(Boolean);

    //     const itemPrincipal = itemSelecionado;

    //     let itensConjunto = itensResolvidos.filter(
    //       (item) =>
    //         !(
    //           String(item.idItem) === String(itemSelecionado.idItem) &&
    //           Number(item.idMaterial) === Number(itemSelecionado.idMaterial)
    //         )
    //     );

    //     const deveLevarItemOrigem =
    //       conjuntoReferencia?.levaItemOri === true ||
    //       conjuntoReferencia?.levaItemOri === 1 ||
    //       conjuntoReferencia?.levaItemOri === '1';

    //     if (
    //       deveLevarItemOrigem &&
    //       itemOrigemConjunto &&
    //       !(
    //         String(itemOrigemConjunto.idItem) ===
    //           String(itemSelecionado.idItem) &&
    //         Number(itemOrigemConjunto.idMaterial) ===
    //           Number(itemSelecionado.idMaterial)
    //       )
    //     ) {
    //       const referenciaJaExiste = itensConjunto.some(
    //         (item) =>
    //           String(item.idItem) === String(itemOrigemConjunto.idItem) &&
    //           Number(item.idMaterial) === Number(itemOrigemConjunto.idMaterial)
    //       );

    //       if (!referenciaJaExiste) {
    //         itensConjunto = [itemOrigemConjunto, ...itensConjunto];
    //       }
    //     }

    //     return {
    //       itemPrincipal: {
    //         ...itemPrincipal,
    //         imagemCatalogo: pagina?.url || itemPrincipal.imagemCatalogo,
    //       },
    //       itensConjunto: itensConjunto.map((item) => ({
    //         ...item,
    //         imagemCatalogo: pagina?.url || item.imagemCatalogo,
    //       })),
    //     };
    //   },
    //   [encontrarItemNaPagina, itensPagina, pagina]
    // );

    const normalizaLevaItemOri = (valor) =>
      valor === true || valor === 1 || valor === '1';

    const mesmoItemMaterial = (itemA, itemB) =>
      String(itemA?.idItem) === String(itemB?.idItem) &&
      Number(itemA?.idMaterial) === Number(itemB?.idMaterial);

    const montarItensConjunto = useCallback(
      (itemSelecionado) => {
        if (!itemSelecionado) {
          return {
            itemPrincipal: null,
            itensConjunto: [],
          };
        }

        const conjuntoReferencia =
          itemSelecionado.conjuntoReferencia ||
          itensPagina.find(
            (itemPagina) =>
              itemPagina.isConjunto &&
              itemPagina.conjuntoItens?.some(
                (filho) =>
                  String(filho.idItem) === String(itemSelecionado.idItem) &&
                  Number(filho.idMaterial) ===
                    Number(itemSelecionado.idMaterial)
              )
          )?.conjuntoReferencia;

        const itemPrincipal = {
          ...itemSelecionado,
          imagemCatalogo: pagina?.url || itemSelecionado.imagemCatalogo,
        };

        if (!conjuntoReferencia) {
          return {
            itemPrincipal,
            itensConjunto: [],
          };
        }

        const deveLevarItemOrigem = normalizaLevaItemOri(
          conjuntoReferencia.levaItemOri
        );

        const itemSelecionadoEhOrigem =
          String(conjuntoReferencia.idItemOri) ===
            String(itemSelecionado.idItem) &&
          Number(conjuntoReferencia.idMaterialOri) ===
            Number(itemSelecionado.idMaterial);

        /**
         * REGRA NOVA:
         * Se levaItemOri for false/0/'0' e o clique NÃO foi no item origem,
         * não deve trazer kit sugerido. Mostra somente o item clicado.
         */
        if (!deveLevarItemOrigem && !itemSelecionadoEhOrigem) {
          return {
            itemPrincipal,
            itensConjunto: [],
          };
        }

        const itemOrigemConjunto = encontrarItemNaPagina({
          idItem: conjuntoReferencia.idItemOri,
          idMaterial: conjuntoReferencia.idMaterialOri,
        });

        const listaBaseConjunto =
          itemOrigemConjunto?.conjuntoItens ||
          itemSelecionado?.conjuntoItens ||
          [];

        let itensConjunto = listaBaseConjunto
          .map((conjuntoItem) => encontrarItemNaPagina(conjuntoItem))
          .filter(Boolean)
          .filter((item) => !mesmoItemMaterial(item, itemSelecionado));

        /**
         * Se levaItemOri for true e o clique foi em um filho,
         * inclui a origem como sugestão também.
         */
        if (
          deveLevarItemOrigem &&
          itemOrigemConjunto &&
          !mesmoItemMaterial(itemOrigemConjunto, itemSelecionado)
        ) {
          const origemJaExiste = itensConjunto.some((item) =>
            mesmoItemMaterial(item, itemOrigemConjunto)
          );

          if (!origemJaExiste) {
            itensConjunto = [itemOrigemConjunto, ...itensConjunto];
          }
        }

        return {
          itemPrincipal,
          itensConjunto: itensConjunto.map((item) => ({
            ...item,
            imagemCatalogo: pagina?.url || item.imagemCatalogo,
          })),
        };
      },
      [encontrarItemNaPagina, itensPagina, pagina]
    );

    const marcarItemPrincipal = useCallback((idItem) => {
      const relacionamentos = mappingRef.current.filter(
        (item) => String(item.idItem) === String(idItem)
      );

      relacionamentos.forEach((relacionamento) => {
        relacionamento.node.classList.remove('selecting-conjunto');
        relacionamento.node.classList.add('selecting');
        relacionamento.selected = true;
      });
    }, []);

    const destacarItensConjunto = useCallback(
      (itensConjunto = [], idItemPrincipal = null) => {
        itensConjunto.forEach((itemConjunto) => {
          if (!itemConjunto?.idItem) return;
          if (String(itemConjunto.idItem) === String(idItemPrincipal)) return;

          const relacionamentos = mappingRef.current.filter(
            (item) => String(item.idItem) === String(itemConjunto.idItem)
          );

          relacionamentos.forEach((relacionamento) => {
            if (!relacionamento.selected) {
              relacionamento.node.classList.remove('selecting');
              relacionamento.node.classList.add('selecting-conjunto');
            }
          });
        });
      },
      []
    );

    const atualizarDestaqueConjuntoSelecionado = useCallback(
      (itensSelecionados = [], idItemPrincipal = null) => {
        mappingRef.current.forEach((relacionamento) => {
          relacionamento.node.classList.remove('selecting-conjunto');
        });

        destacarItensConjunto(itensSelecionados, idItemPrincipal);
      },
      [destacarItensConjunto]
    );

    // const handleAdicionar = useCallback(
    //   (itemAdicionar) => {
    //     const {
    //       idItem,
    //       idMaterial,
    //       quantidade,
    //       materialEmpresa,
    //     } = itemAdicionar;

    //     zoomResetRef.current.onclick();

    //     itensPagina.forEach((item) => {
    //       if (item.selecionado !== false) item.selecionado = false;
    //     });

    //     itemAdicionar.selecionado = true;
    //     itemAdicionar.imagemCatalogo = pagina.url;

    //     /** limpa seleções anteriores que não foram adicionadas no carrinho */
    //     mappingRef.current.forEach((relacionamento) => {
    //       if (!relacionamento.inCart) {
    //         relacionamento.selected = false;
    //         relacionamento.node.classList.remove('selecting');
    //       }
    //     });

    //     const relacionamentos = mappingRef.current.filter(
    //       (item) => item.idItem === idItem
    //     );

    //     relacionamentos.forEach((relacionamento) => {
    //       relacionamento.node.classList.add('selecting');
    //       relacionamento.selected = true;
    //     });

    //     /** verifica quantidade de material ERP e parâmetro para adicionar automaticamente para o carrinho */
    //     if (materialEmpresa.length === 1 && redirectToCart()) {
    //       /** caso existe material relacionado, pega o código */
    //       const { codimate } = materialEmpresa[0];
    //       const imagemCatalogo = pagina.url;
    //       dispatch(
    //         adicionaCarrinhoRequest(
    //           id,
    //           idPagina,
    //           idItem,
    //           idMaterial,
    //           quantidade,
    //           codimate,
    //           imagemCatalogo
    //         )
    //       );

    //       relacionamentos.forEach((relacionamento) => {
    //         relacionamento.inCart = true;
    //       });

    //       return;
    //     }

    //     /** caso tenha mais de um material relacionado, abre o modal */
    //     setItemCart(itemAdicionar);
    //   },
    //   [id, idPagina, dispatch, itensPagina, pagina, zoomResetRef]
    // );

    const handleAdicionar = useCallback(
      (itemAdicionar) => {
        const {
          idItem,
          idMaterial,
          quantidade,
          materialEmpresa,
          /**
           * IDCATALOGO/IDPAGINA reais do registro em ITEMPAGINA. Em um face
           * do sistema o item pode estar listado (mesma "tabela") em uma
           * página diferente da que está sendo visualizada — por isso não
           * se pode assumir que o item pertence à página atual (id/idPagina
           * da rota). Sempre usa a página real do item para localizá-lo.
           */
          idCatalogo: idCatalogoItem,
          idPagina: idPaginaItem,
        } = itemAdicionar;

        zoomResetRef.current.onclick();

        itensPagina.forEach((item) => {
          if (item.selecionado !== false) item.selecionado = false;
        });

        itemAdicionar.selecionado = true;
        itemAdicionar.imagemCatalogo = pagina.url;

        /** limpa seleções anteriores que não foram adicionadas no carrinho */
        // mappingRef.current.forEach((relacionamento) => {
        //   if (!relacionamento.inCart) {
        //     relacionamento.selected = false;
        //     relacionamento.node.classList.remove('selecting');
        //   }
        // });

        // const relacionamentos = mappingRef.current.filter(
        //   (item) => item.idItem === idItem
        // );

        // relacionamentos.forEach((relacionamento) => {
        //   relacionamento.node.classList.add('selecting');
        //   relacionamento.selected = true;
        // });

        limparSelecoesTemporarias();

        const { itemPrincipal, itensConjunto } = montarItensConjunto(
          itemAdicionar
        );

        if (itemPrincipal?.idItem) {
          marcarItemPrincipal(itemPrincipal.idItem);
        }

        destacarItensConjunto(itensConjunto, itemPrincipal?.idItem);

        // const { itemPrincipal, itensConjunto } = montarItensConjunto(
        //   itemAdicionar
        // );

        const possuiConjunto = itensConjunto.length > 0;

        if (
          !possuiConjunto &&
          materialEmpresa.length === 1 &&
          redirectToCart()
        ) {
          const { codimate } = materialEmpresa[0];
          const imagemCatalogo = pagina.url;

          dispatch(
            adicionaCarrinhoRequest(
              idCatalogoItem || id,
              idPaginaItem ?? idPagina,
              idItem,
              idMaterial,
              quantidade,
              codimate,
              imagemCatalogo
            )
          );

          // relacionamentos.forEach((relacionamento) => {
          //   relacionamento.inCart = true;
          // });

          const relacionamentosPrincipal = mappingRef.current.filter(
            (item) =>
              String(item.idItem) === String(itemPrincipal?.idItem || idItem)
          );

          relacionamentosPrincipal.forEach((relacionamento) => {
            relacionamento.inCart = true;
            relacionamento.selected = true;
          });

          return;
        }

        // setItemCart({
        //   ...itemAdicionar,
        //   itensConjuntoModal: itensConjunto,
        // });

        setItensConjuntoSelecionados(itensConjunto);

        setItemCart({
          ...(itemPrincipal || itemAdicionar),
          itensConjuntoModal: itensConjunto,
          itensConjuntoSelecionados: itensConjunto,
        });
      },
      [
        id,
        idPagina,
        dispatch,
        itensPagina,
        pagina,
        zoomResetRef,
        montarItensConjunto,
      ]
    );

    const handleLinkClick = useCallback(
      (e, idItem) => {
        if (e) e.preventDefault();

        // const [, , , idItem] = e.target.parentElement.href.baseVal.split('/');

        const itemIndex = itensPagina.findIndex(
          (item) => item.idItem === idItem
        );

        if (itemIndex >= 0) {
          itensPagina[itemIndex].exibido = true;

          /** tabela reduzida: mantém o item clicado como item ativo */
          if (tabelaReduzida) {
            setItemAtivoChave(chaveItemPagina(itensPagina[itemIndex]));
          }

          /** limpa seleção da tabela */
          itensPagina.forEach((item) => {
            if (item.idItem !== idItem) item.selecionado = false;
          });

          // const relacionamentos = mappingRef.current.filter(
          //   (item) => item.idItem === idItem
          // );
          // relacionamentos.forEach((relacionamento) => {
          //   relacionamento.node.classList.add('selecting');
          //   relacionamento.selected = true;
          // });

          /** oculta informativo e exibe o modal */
          setItemInfo(null);

          handleAdicionar(itensPagina[itemIndex]);

          // setItemCart(itensPagina[itemIndex]);
        }
      },
      [itensPagina, handleAdicionar, tabelaReduzida]
    );

    const handleLinkMouseEnter = useCallback(
      (e, idItem) => {
        e.preventDefault();

        // const [, , , idItem] = e.target.href.baseVal.split('/');

        const itemIndex = itensPagina.findIndex(
          (item) => item.idItem === idItem
        );
        if (itemIndex >= 0) {
          itensPagina[itemIndex].selecionado = true;

          /**
           * Tabela reduzida (parâmetro 34): o item percorrido no desenho passa
           * a ser o item exibido na listagem e permanece após o hover.
           */
          if (tabelaReduzida) {
            setItemAtivoChave(chaveItemPagina(itensPagina[itemIndex]));
          }

          const relacionamentos = mappingRef.current.filter(
            (item) => item.idItem === idItem
          );
          relacionamentos.forEach((relacionamento) => {
            relacionamento.node.classList.add('selecting');
          });

          const {
            left: leftContainer,
            top: topContainer,
          } = containerRef.current.getBoundingClientRect();

          const a = e.target.getBoundingClientRect();

          setItemInfo({
            ...itensPagina[itemIndex],
            posX: a.left - leftContainer - 90,
            posY: a.top - topContainer + a.height + 5,
          });

          /** reposiciona listagem de itens (não se aplica à tabela reduzida) */
          if (!tabelaReduzida && tableRef.current) {
            let currRow = null;

            tableRef.current.childNodes.forEach((node) => {
              const [, rowIdItem, rowIdMaterial] = node.id.split('-');

              if (
                String(itensPagina[itemIndex].idItem) === String(rowIdItem) &&
                Number(itensPagina[itemIndex].idMaterial) ===
                  Number(rowIdMaterial)
              ) {
                currRow = node;
              }
            });

            if (currRow) {
              const { height = 0 } = tableRef.current.getBoundingClientRect();

              tableRef.current.scrollTo({
                top: currRow.offsetTop - Math.trunc(height / 2),
                behavior: 'smooth',
              });
            }
          }
        }
      },
      [itensPagina, tabelaReduzida]
    );

    const handleLinkMouseOut = useCallback(
      (e, idItem) => {
        e.preventDefault();

        const itemIndex = itensPagina.findIndex(
          (item) => item.idItem === idItem
        );
        if (itemIndex >= 0) {
          /** limpa seleção da tabela */
          itensPagina.forEach((item) => {
            if (!item.exibido) item.selecionado = false;
          });

          const relacionamentos = mappingRef.current.filter(
            (item) => item.idItem === idItem
          );

          relacionamentos.forEach((relacionamento) => {
            // if (!relacionamento.selected && !relacionamento.inCart) {
            //   relacionamento.node.classList.remove('selecting');
            // }
            if (
              !relacionamento.selected &&
              !relacionamento.inCart &&
              !relacionamento.node.classList.contains('selecting-conjunto')
            ) {
              relacionamento.node.classList.remove('selecting');
            }
          });

          /** oculta informativo */
          setItemInfo(null);
        }
      },
      [itensPagina]
    );

    const handleLink = useCallback(
      (childNodes) => {
        childNodes.forEach((node) => {
          if (node.nodeName === 'a') {
            const [, , , idItem] = node.href.baseVal.split('/');

            mappingRef.current.push({
              idItem,
              node,
              inCart: false,
              selected: false,
            });

            // node.addEventListener('click', (e) => handleLinkClick(e, idItem));
            node.onclick = (e) => handleLinkClick(e, idItem);

            node.addEventListener('mouseenter', (e) =>
              handleLinkMouseEnter(e, idItem)
            );

            node.addEventListener('mouseout', (e) => {
              /** previne a saída quando o mouse está sobre o texto */
              if (e.relatedTarget.parentElement !== e.target.parentElement) {
                handleLinkMouseOut(e, idItem);
              }
            });
          }

          /** processa nós do svg */
          if (node.hasChildNodes()) {
            return handleLink(node.childNodes);
          }

          return true;
        });
      },
      [handleLinkClick, handleLinkMouseEnter, handleLinkMouseOut]
    );

    const editorSvg = useMemo(
      () => (
        <SVG
          src={pagina && pagina.url}
          loading={() => <span>Loading</span>}
          useRequestCache={false}
          afterInjection={(err, svg) => {
            if (svg) {
              mappingRef.current = [];
              handleLink(svg.childNodes);
              handleRelacionar();

              // svg.setAttribute('width', '420');
              // svg.setAttribute('height', '390');

              // svg.setAttribute('viewBox', '0 0 750 850');
              svg.removeAttribute('width');
              svg.removeAttribute('height');

              svgRef.current = svg;

              /** gera uma cópia da imagem (data URI) para a lupa de zoom */
              try {
                const clone = svg.cloneNode(true);
                if (!clone.getAttribute('viewBox')) {
                  const { width, height } = svg.getBoundingClientRect();
                  if (width && height) {
                    clone.setAttribute('width', width);
                    clone.setAttribute('height', height);
                  }
                }
                const xml = new XMLSerializer().serializeToString(clone);
                setMagnifierSrc(
                  `data:image/svg+xml;charset=utf-8,${encodeURIComponent(xml)}`
                );
              } catch (serializeErr) {
                setMagnifierSrc(null);
              }

              setTimeout(() => {
                if (zoomResetRef.current) {
                  zoomResetRef.current.onclick();
                }
              }, 100);
            }
          }}
        />
      ),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [pagina, handleLink, zoomResetRef]
    );

    const [noteStyle, spring] = useSpring(() => ({
      config: { mass: 2, tension: 80, friction: 25, precision: 0.001 },
    }));

    useEffect(() => {
      if (showNote) {
        lockScroll();
      } else {
        unlockScroll();
      }

      spring.start({
        opacity: showNote ? 1 : 0,
        scale: showNote ? 1 : 0,
      });

      return () => unlockScroll();
    }, [showNote, spring, lockScroll, unlockScroll]);

    useEffect(() => {
      if (itemCart) {
        window.onclick = (e) => {
          if (
            e.target.tagName === 'svg:circle' ||
            e.target.tagName === 'svg:square'
          ) {
            setElementToZoom(e);
          }
          setElementToZoom(null);
        };
        setElementToZoom(null);
      }
    }, [itemCart]);

    const handleImageMouseMove = useCallback(
      (e) => {
        if (!lupaAtiva || !magnifierSrc) return;

        const svgEl = svgRef.current;
        const lensEl = lensRef.current;
        const viewEl = lensViewRef.current;
        const imgEl = lensImgRef.current;
        const containerEl = containerRef.current;
        if (!svgEl || !lensEl || !viewEl || !imgEl || !containerEl) return;

        /** durante o pan (arrasto com o botão pressionado) a lupa fica oculta */
        if (e.buttons !== 0) {
          lensEl.style.display = 'none';
          return;
        }

        const svgRect = svgEl.getBoundingClientRect();
        const contRect = containerEl.getBoundingClientRect();

        /** fora da área da imagem -> oculta a lupa */
        if (
          !svgRect.width ||
          !svgRect.height ||
          e.clientX < svgRect.left ||
          e.clientX > svgRect.right ||
          e.clientY < svgRect.top ||
          e.clientY > svgRect.bottom
        ) {
          lensEl.style.display = 'none';
          return;
        }

        /** posição relativa (0..1) na imagem, invariante ao pan/zoom atual */
        const fx = (e.clientX - svgRect.left) / svgRect.width;
        const fy = (e.clientY - svgRect.top) / svgRect.height;

        /** cópia ampliada: LENS_ZOOM x além do zoom já aplicado em tela */
        const zoomW = svgRect.width * LENS_ZOOM;
        const zoomH = svgRect.height * LENS_ZOOM;

        imgEl.style.width = `${zoomW}px`;
        imgEl.style.height = `${zoomH}px`;
        imgEl.style.left = `${LENS_SIZE / 2 - fx * zoomW}px`;
        imgEl.style.top = `${LENS_SIZE / 2 - fy * zoomH}px`;

        /** posiciona a lupa logo ao lado do cursor, contida no container */
        const cx = e.clientX - contRect.left;
        const cy = e.clientY - contRect.top;

        let lensLeft = cx + 24;
        if (lensLeft + LENS_TOTAL_W > contRect.width) {
          lensLeft = cx - 24 - LENS_TOTAL_W;
        }
        /** container estreito: o lado oposto também pode não caber */
        if (lensLeft < 0) lensLeft = 0;

        let lensTop = cy - LENS_SIZE / 2;
        if (lensTop < 0) lensTop = 0;
        /**
         * A contenção usa a altura TOTAL (área ampliada + faixa + moldura),
         * mesmo com a faixa invisível. Assim o cartão não se mexe ao entrar
         * e sair de um link, e a faixa nunca é cortada na borda de baixo.
         */
        if (lensTop + LENS_TOTAL_H > contRect.height) {
          lensTop = contRect.height - LENS_TOTAL_H;
        }
        if (lensTop < 0) lensTop = 0;

        /** a área ampliada é quadrada; a faixa tem altura própria e fixa */
        viewEl.style.width = `${LENS_SIZE}px`;
        viewEl.style.height = `${LENS_SIZE}px`;

        /** medida externa: o box-sizing border-box está fixado em styles.js */
        lensEl.style.width = `${LENS_TOTAL_W}px`;
        lensEl.style.left = `${lensLeft}px`;
        lensEl.style.top = `${lensTop}px`;
        /** flex, e não block: o cartão empilha área ampliada + faixa */
        lensEl.style.display = 'flex';
      },
      [lupaAtiva, magnifierSrc]
    );

    const handleImageMouseLeave = useCallback(() => {
      if (lensRef.current) lensRef.current.style.display = 'none';
    }, []);

    /**
     * Lupa efetivamente em uso. Só o estado do botão não basta: sem o
     * magnifierSrc (falha ao serializar o SVG) o cartão nunca é montado,
     * e nesse caso a caixa flutuante do hover precisa continuar valendo.
     */
    const lupaVisivel = lupaAtiva && !!magnifierSrc;

    const handleToggleLupa = useCallback(() => {
      setLupaAtiva((prev) => {
        const next = !prev;
        /** ao desligar, oculta a lupa imediatamente */
        if (!next && lensRef.current) {
          lensRef.current.style.display = 'none';
        }
        return next;
      });
    }, []);

    const renderizaNivel = (item) => {
      const niveis = [];
      for (let i = 0; i < item.nivelConjuntoItemPagina; i += 1) {
        niveis.push(
          <span key={i}>
            <MdLens size={8} id="lens" title="Item do conjunto" />
          </span>
        );
      }
      return niveis;
    };

    const carregaItensObserver = useCallback(() => {
      if (currentPage < totalPage) {
        api
          .get(`/catalogo-publico/${id}/pagina/${idPagina}/item`, {
            params: {
              page: currentPage + 1,
            },
          })
          .then((response) => {
            const add = response.data.responseItemPagina.map((item) => {
              const { idItem, materialEmpresa, materialFabricante } = item;

              /** formata IDITEM */
              const fmtItem =
                String(idItem).startsWith('@') || String(idItem) === '0'
                  ? ''
                  : idItem;

              /** formata PARTNUMBER */
              const fmtPartnumber = String(
                materialFabricante.partnumber
              ).startsWith('@')
                ? ''
                : materialFabricante.partnumber;

              return {
                ...item,
                materialEmpresa,
                ...materialFabricante,
                partnumber: fmtPartnumber,
                itemResum: fmtItem,

                // Concatena os materiais da empresa
                codimateResum:
                  materialEmpresa &&
                  materialEmpresa
                    .map((material) => material.codimate)
                    .join(', '),
                selecionado: false,
                exibido: false,
              };
            });

            add.forEach((addItem) => {
              if (!itensPagina.find((item) => addItem.idItem === item.idItem)) {
                setItensPagina((prevState) => [...prevState, ...add]);
              }
            });

            setCurrentPage(response.data.pagAtual);
          });
      }
    }, [currentPage, totalPage, id, idPagina, itensPagina]);

    useEffect(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            carregaItensObserver();
          }
        },
        { threshold: 0 }
      );

      if (observerTarget.current) {
        observer.observe(observerTarget.current);
      }

      // return () => {
      //   if (observerTarget.current) {
      //     /** Ponto de desativacao do observer */
      //     observer.unobserve(observerTarget.current)
      //   }
      // }
    }, [observerTarget, carregaItensObserver]);

    // console.log(itensPagina);
    // console.log('itemCart: ', itemCart);
    // console.log('containerRef: ', containerRef);

    // const handleItensConjuntoSelecionadosChange = useCallback(
    //   (itensSelecionados = []) => {
    //     setItensConjuntoSelecionados(itensSelecionados);

    //     if (!itemCart) return;

    //     atualizarDestaqueConjuntoSelecionado(
    //       itensSelecionados,
    //       itemCart.idItem
    //     );

    //     setItemCart((prevState) =>
    //       prevState
    //         ? { ...prevState, itensConjuntoSelecionados: itensSelecionados }
    //         : prevState
    //     );
    //   },
    //   [itemCart, atualizarDestaqueConjuntoSelecionado]
    // );

    useEffect(() => {
      if (!itemCart) return;

      atualizarDestaqueConjuntoSelecionado(
        itensConjuntoSelecionados,
        itemCart.idItem
      );
    }, [
      itemCart,
      itensConjuntoSelecionados,
      atualizarDestaqueConjuntoSelecionado,
    ]);

    useEffect(() => {
      if (!itemCart) setItensConjuntoSelecionados([]);
    }, [itemCart]);

    return (
      <Container>
        {pagina && (
          <Wrapper>
            <ZoomArea
              ref={containerRef}
              reduzida={tabelaReduzida ? 1 : 0}
              onMouseMove={handleImageMouseMove}
              onMouseLeave={handleImageMouseLeave}
            >
              <TransformWrapper
                initialScale={2}
                initialPositionX={-500}
                initialPositionY={-100}
                centerOnInit
                centerZoomedOut
                doubleClick={{
                  step: 0.7,
                  animationTime: 500,
                  animationType: 'easeInOutQuad',
                }}
                // wheel={{ step: zoomStep }}
              >
                {({
                  zoomIn,
                  zoomOut,
                  resetTransform,
                  setTransform,
                  centerView,
                }) => {
                  if (
                    zoomInRef &&
                    zoomInRef.current &&
                    !zoomInRef.current.onclick
                  ) {
                    zoomInRef.current.onclick = () => zoomIn(2, 500, 'easeOut');
                  }

                  if (
                    zoomOutRef &&
                    zoomOutRef.current &&
                    !zoomOutRef.current.onclick
                  ) {
                    zoomOutRef.current.onclick = () =>
                      zoomOut(0.2, 500, 'easeOut');
                  }

                  if (
                    zoomResetRef &&
                    zoomResetRef.current &&
                    !zoomResetRef.current.onclick
                  ) {
                    zoomResetRef.current.onclick = () =>
                      resetTransform(500, 'easeOut');
                  }

                  if (itemCart && elementToZoom) {
                    /*
                    setTransform(
                      -Math.abs(elementToZoom.layerX),
                      -Math.abs(elementToZoom.layerY),
                      2,
                      1000,
                      'easeInOutQuad'
                    );
                    centerView(1.5, 1000, 'easeInOutQuad');
                    */
                  }

                  return (
                    <TransformComponent>
                      {currentPage === totalPage ? (
                        editorSvg
                      ) : (
                        <span>Loading</span>
                      )}
                    </TransformComponent>
                  );
                }}
              </TransformWrapper>

              {itemNote.length > 0 && (
                <NoteContainer
                  onMouseDown={() => setShowNote((state) => !state)}
                >
                  <NoteContent>
                    <Notes>
                      <p>
                        {itemNote[0]}
                        {itemNote[1] && '...'}
                      </p>
                    </Notes>
                  </NoteContent>
                </NoteContainer>
              )}

              <NotesModal
                style={{
                  opacity: noteStyle.opacity,
                  scale: noteStyle.scale,
                }}
                sizenote={lengthNote}
              >
                <NoteHeader>
                  <strong>NOTAS DA PÁGINA</strong>
                  <button
                    type="button"
                    onClick={() => setShowNote((state) => !state)}
                  >
                    <MdClose size={25} />
                  </button>
                </NoteHeader>

                <ul>
                  {!!itemNote.length &&
                    itemNote.map((item, index) => <p key={index}>{item}</p>)}
                </ul>
              </NotesModal>

              {/**
               * Caixa flutuante do hover, usada apenas com a lupa
               * DESLIGADA. Com a lupa ligada a mesma informação vai para
               * a faixa do cartão da lupa: manter as duas geraria
               * informação repetida, e esta ficaria escondida atrás da
               * lupa de qualquer forma (z-index 30 contra z-index auto).
               */}
              {itemInfo && !lupaVisivel && (
                <Info posX={itemInfo.posX} posY={itemInfo.posY}>
                  <strong>{itemInfo.descricao}</strong>
                  <p>{itemInfo.partnumber}</p>
                </Info>
              )}

              {/* {itemCart && (
                <ItemCart
                  containerRef={containerRef}
                  item={itemCart}
                  onClose={handleItemFechar}
                />
              )} */}
              {itemCart && (
                <ItemCart
                  containerRef={containerRef}
                  item={itemCart}
                  itensPagina={itensPagina}
                  onClose={handleItemFechar}
                />
              )}

              {magnifierSrc && (
                <LupaToggle
                  type="button"
                  active={lupaAtiva ? 1 : 0}
                  onClick={handleToggleLupa}
                  title={lupaAtiva ? 'Desativar lupa' : 'Ativar lupa'}
                  aria-pressed={lupaAtiva}
                >
                  <MdSearch size={18} />
                </LupaToggle>
              )}

              {lupaAtiva && magnifierSrc && (
                <MagnifierLens ref={lensRef}>
                  <MagnifierLens.View ref={lensViewRef}>
                    <img ref={lensImgRef} src={magnifierSrc} alt="" />
                  </MagnifierLens.View>

                  {/**
                   * Faixa de informação do item sob o cursor.
                   *
                   * Fica sempre montada, com altura reservada, e apenas
                   * troca de visibilidade: se entrasse e saísse do
                   * layout, o cartão mudaria de altura a cada link e a
                   * área ampliada se deslocaria.
                   */}
                  {/**
                   * visivel em 1/0, e não booleano: styled-components
                   * repassa a prop para o DOM e o React reclama de
                   * atributo não booleano recebendo true. Mesmo padrão
                   * já usado em LupaToggle (active={... ? 1 : 0}).
                   */}
                  <MagnifierLens.Info
                    visivel={itemInfo ? 1 : 0}
                    style={{ height: LENS_INFO_H }}
                  >
                    <strong>{itemInfo ? itemInfo.descricao : ''}</strong>
                    <span>{itemInfo ? itemInfo.partnumber : ''}</span>
                  </MagnifierLens.Info>
                </MagnifierLens>
              )}
            </ZoomArea>
          </Wrapper>
        )}

        {!paginaMaximizada && (
          <ItemArea>
            {itensPagina && (
              <Table
                reduzida={tabelaReduzida ? 1 : 0}
                semErp={ocultaERPHome ? 1 : 0}
              >
                <thead>
                  <tr>
                    <th>{HEADER_TABLE_ITENS_IT[idioma]}</th>
                    <th>{HEADER_TABLE_ITENS_PN[idioma]}</th>
                    {!ocultaERPHome && (
                      <th>{HEADER_TABLE_ITENS_CERP[idioma]}</th>
                    )}
                    <th>{HEADER_TABLE_ITENS_DESC[idioma]}</th>
                    <th>{HEADER_TABLE_ITENS_QTD[idioma]}</th>
                    <th>{HEADER_TABLE_ITENS_NT[idioma]}</th>
                    <th>{HEADER_TABLE_ITENS_ADD[idioma]}</th>
                  </tr>
                </thead>
                <tbody ref={tableRef}>
                  {itensExibidos.map((item, index) => (
                    <Table.Row
                      id={`row-${item.idItem}-${item.idMaterial}`}
                      onMouseEnter={() => handleItemMouseEnter(item.idItem)}
                      onMouseLeave={() => handleItemMouseLeave(item.idItem)}
                      selected={item.selecionado}
                      key={index}
                    >
                      <td title={item.itemResum}>
                        <div>
                          {/* {renderizaNivel(item)} */}
                          {item.isConjunto && (
                            <span>
                              <MdLabelOutline size={12} title="Conjunto" />
                            </span>
                          )}
                          {item.itemResum}
                        </div>
                      </td>
                      <td title={item.partnumber}>
                        <div
                          style={{
                            display: '1',
                            gap: '2px',
                            flexDirection: 'row',
                          }}
                        >
                          {item.partnumber}
                          {item.partnumberAlterado && (
                            <IoGitPullRequestSharp size={12} color="#020617" />
                          )}
                        </div>
                      </td>
                      {!ocultaERPHome && (
                        <td title={item.codimateResum}>{item.codimateResum}</td>
                      )}
                      {/** title exibe a descrição completa quando truncada */}
                      <td title={item.descricao}>{item.descricao}</td>
                      <td>{item.quantidade}</td>
                      <td title={item.nota}>{item.nota}</td>
                      <td>
                        <Table.BtnCart
                          inCart={
                            !!carrinho.find(
                              (itemCarrinho) =>
                                Number(itemCarrinho.idCatalogo) ===
                                  Number(item.idCatalogo) &&
                                mesmaPaginaOuFace(itemCarrinho, item) &&
                                String(itemCarrinho.idItem) ===
                                  String(item.idItem) &&
                                Number(itemCarrinho.idMaterial) ===
                                  Number(item.idMaterial)
                            )
                          }
                          type="button"
                          title="Adicionar ao carrinho"
                          onClick={() => handleAdicionar(item)}
                          // onClick={() => console.log('onClick, item: ', item)}
                        >
                          <MdShoppingCart size={18} />
                        </Table.BtnCart>
                      </td>
                    </Table.Row>
                  ))}
                </tbody>
              </Table>
            )}
            {currentPage < totalPage && <div ref={observerTarget} />}
          </ItemArea>
        )}
      </Container>
    );
  }
);

export default Pagina;
